import { Add as AddIcon, Save as SaveIcon } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid";
import { unwrapResult } from "@reduxjs/toolkit";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import { SyncStockPart } from "components/stock/SyncStockPart";
import StyledFab from "components/StyledFab";
import { StockSync } from "models/StockSync";
import { TabProps } from "models/TabProps";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "store";
import {
  getStockList,
  getTechStock,
  orderMobileStock,
  removeStockToOrder,
  updateStockToOrder,
} from "store/slices/stock.store";
import { SelectStockToOrderDialog } from "./SelectStockToOrderDialog";

export const StockOrderTab: FC<TabProps> = ({ setFab }) => {
  const dispatch = useAppDispatch();
  const { loadingOrderStock, stockToOrder } = useAppSelector((s) => s.stock);
  const [openSelectDialog, setOpenSelectDialog] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getStockList());
  }, [dispatch]);

  const onOrderPart = useCallback(
    (request: StockSync) => {
      dispatch(
        updateStockToOrder({
          stock: request,
        }),
      );
    },
    [dispatch],
  );

  const onRemovePart = useCallback(
    (request: StockSync) => {
      dispatch(
        removeStockToOrder({
          stock: request,
        }),
      );
    },
    [dispatch],
  );

  const partListItems = useMemo(() => {
    return stockToOrder.map((stock) => {
      return (
        stock && (
          <SyncStockPart
            stock={stock}
            key={`${stock.sync.articleNo}`}
            onSyncPart={onOrderPart}
            onRemovePart={onRemovePart}
          />
        )
      );
    });
  }, [onOrderPart, onRemovePart, stockToOrder]);

  const onSave = useCallback(() => {
    dispatch(
      orderMobileStock({
        stocks: stockToOrder.map((s) => s.sync),
      }),
    )
      .then(unwrapResult)
      .then((_) => {
        dispatch(getTechStock());
      });
  }, [dispatch, stockToOrder]);

  useEffect(() => {
    const fab = (
      <>
        <Grid item>
          <StyledFab onClick={() => setOpenSelectDialog(true)}>
            <AddIcon />
          </StyledFab>
        </Grid>
        <Grid item>
          <StyledFab onClick={() => onSave} disabled={stockToOrder.length === 0}>
            <SaveIcon />
          </StyledFab>
        </Grid>
      </>
    );
    if (setFab) {
      setFab(fab);
    }
  }, [dispatch, onSave, setFab, stockToOrder.length]);

  return (
    <Grid container direction="column" data-testid="StockMoveTab">
      <BackdropPrimaryMain open={loadingOrderStock}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <Grid container spacing={1}>
        {partListItems && partListItems?.length > 0 ? partListItems : null}
      </Grid>
      <SelectStockToOrderDialog
        open={openSelectDialog}
        setOpenAddPartDialog={setOpenSelectDialog}
        handleClose={() => setOpenSelectDialog(false)}
      />
    </Grid>
  );
};
